/**
 * Tiny localStorage-backed auth token store.
 *
 * Session 1.1 only needs to read an access token from somewhere so the API
 * client can attach it to outgoing requests. The login screen, refresh-token
 * rotation and MFA flow live in a later session — for now this file just
 * exposes a get/set/clear surface and reads from localStorage on demand.
 *
 * The key name (`jeanieiq.accessToken`) is namespaced so it never collides
 * with other apps the user might run on the same origin.
 *
 * Brand-rename note (Phase 1.1): the key was previously `base.accessToken`.
 * On read we fall back to the legacy key once and migrate to the new key,
 * so existing logged-in users don't get bounced to the login screen on
 * deploy. The fallback is removed in a future cleanup once everyone has
 * naturally cycled.
 */

const ACCESS_TOKEN_KEY = 'jeanieiq.accessToken';
const LEGACY_ACCESS_TOKEN_KEY = 'base.accessToken';

/*
 * THE EMPLOYEE APP KEEPS ITS OWN SESSION (2026-09-24).
 *
 * The Employee app (/employee) and the staff platform are served from the
 * same address, and a browser keeps one localStorage per address — so with a
 * single key they shared ONE session. Signing in to one signed the other out,
 * and worse, a 401 in either tab cleared the token for both: a staff tab still
 * polling in the background with the employee's token got refused, cleared
 * it, and the employee was thrown out mid-screen. Found testing, 2026-09-24;
 * it is also exactly what a manager who is ALSO an employee would hit every
 * day. Reagan: "the employee log ins should be the emails from when they are
 * put on HR and People — the platform log in is what a tenant uses for workers
 * who are using the platform for work stuff."
 *
 * So on the Employee app's own pages the token lives under its own key, and
 * everything that reads, writes or clears "the" token — apiFetch, its 401
 * interceptor, logout, the "Remember me" check — gets that slot there and the
 * staff slot everywhere else. Same accounts, same /api/auth/login, same server
 * checks: only WHERE THE BROWSER KEEPS IT changes. Whether employees should be
 * a separate kind of account altogether is a platform decision, left open.
 *
 * Decided by the page's path at the moment of the call, so it holds for a
 * client-side navigation as well as a full load. Exact match on /employee —
 * `startsWith('/employee')` would also catch /employees, a staff screen.
 */
const EMPLOYEE_ACCESS_TOKEN_KEY = 'jeanieiq.employee.accessToken';

/** True on the Employee app's pages — the one place with its own session. */
export function inEmployeeApp(): boolean {
  if (typeof window === 'undefined') return false;
  const p = window.location.pathname;
  return p === '/employee' || p.startsWith('/employee/');
}

/**
 * Read the current access token from localStorage. Returns null when no
 * token has been stored or when localStorage is unavailable (server render,
 * private mode, etc.).
 *
 * Migration: if the new key is missing but the legacy `base.accessToken`
 * key has a value, we move the value to the new key and remove the legacy
 * one. Existing logged-in users transition transparently.
 */
export function getAccessToken(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    // The Employee app's own slot. No legacy fallback: it never used one.
    if (inEmployeeApp()) return window.localStorage.getItem(EMPLOYEE_ACCESS_TOKEN_KEY);
    const fresh = window.localStorage.getItem(ACCESS_TOKEN_KEY);
    if (fresh !== null) return fresh;
    // Legacy fallback + one-shot migration.
    const legacy = window.localStorage.getItem(LEGACY_ACCESS_TOKEN_KEY);
    if (legacy !== null) {
      window.localStorage.setItem(ACCESS_TOKEN_KEY, legacy);
      window.localStorage.removeItem(LEGACY_ACCESS_TOKEN_KEY);
      return legacy;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Persist an access token to localStorage. No-op when localStorage is
 * unavailable — callers should not rely on this throwing.
 */
export function setAccessToken(token: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(inEmployeeApp() ? EMPLOYEE_ACCESS_TOKEN_KEY : ACCESS_TOKEN_KEY, token);
  } catch {
    /* ignore */
  }
}

/**
 * Remove the access token from localStorage. Used on logout and on 401
 * responses from the API. Clears both the new and legacy keys so a user
 * logging out under either schema is fully signed out.
 */
export function clearAccessToken(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    // Only the slot of the app you are in — signing out of one, or a 401 in
    // one, must never sign the other out.
    if (inEmployeeApp()) {
      window.localStorage.removeItem(EMPLOYEE_ACCESS_TOKEN_KEY);
      return;
    }
    window.localStorage.removeItem(ACCESS_TOKEN_KEY);
    window.localStorage.removeItem(LEGACY_ACCESS_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Shape of the decoded JWT payload. The server signs:
 *   { sub, tenantId, role, tier }
 * plus standard claims (iat, exp). All security gates run on the
 * server — this helper exists only for UX (hide a button you'd be
 * 403'd on anyway, etc.) and MUST NOT be relied on for any
 * authorisation decision.
 */
export interface DecodedAccessToken {
  sub: string;
  tenantId: string;
  role: string;
  tier?: string;
  exp?: number;
  iat?: number;
  /** JN-ADMIN-IMP-001 — id of the platform admin who minted this
   *  impersonation token. Set only on impersonation sessions; absent
   *  for normal user logins. Used to render the impersonation banner. */
  imp?: string;
  /** Email of the impersonating admin (cosmetic, banner only). */
  impEmail?: string;
}

/**
 * Decode the JWT payload WITHOUT verifying the signature. Safe for UI
 * affordance gating only; never trust this for actual authorisation.
 * Returns null on any error (no token, malformed, etc.).
 */
export function decodeAccessToken(): DecodedAccessToken | null {
  const token = getAccessToken();
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    // JWT uses base64url. atob handles base64; convert to standard
    // base64 by replacing - and _ and padding to a multiple of 4.
    const payload = parts[1] ?? '';
    const b64 =
      payload.replace(/-/g, '+').replace(/_/g, '/') +
      '=='.slice(0, (4 - (payload.length % 4)) % 4);
    const json = JSON.parse(atob(b64)) as DecodedAccessToken;
    return json;
  } catch {
    return null;
  }
}

/**
 * Convenience getter for the current user's role (from the access
 * token). Returns null when there's no token or it's malformed.
 *
 * For UX only — server is authoritative.
 */
export function getCurrentUserRole(): string | null {
  return decodeAccessToken()?.role ?? null;
}

/** Approving is for directors and managers; the server also asks for Document Register. */
export function canApproveDocuments(role: string | null): boolean {
  return role === 'director' || role === 'manager';
}

/**
 * Convenience getter for the current user's userId (sub claim).
 */
export function getCurrentUserId(): string | null {
  return decodeAccessToken()?.sub ?? null;
}

/**
 * Clear the session after a 401 — the ONE copy of that decision.
 *
 * 2026-08-11. Fourteen call sites across jeanieApi (9), attachmentsApi (3)
 * and documentRegisterApi (2) each hand-rolled
 *
 *     if (res.status === 401) { clearAccessToken(); throw ... }
 *
 * while api.ts's interceptor carried a guard the copies did not: only act
 * when the request ACTUALLY CARRIED credentials, so a call that deliberately
 * went out unauthenticated cannot tear down a live session. Those files build
 * headers with `if (token) headers['Authorization'] = ...`, so going out
 * without credentials is reachable, not theoretical.
 *
 * Being straight about severity: the copies were not doing visible damage.
 * With no token, clearAccessToken() is a no-op, and none of them redirect, so
 * there was no recursive-redirect risk either. This is consolidation to stop
 * fourteen copies drifting — the moment one of them grows a redirect, the
 * guard stops being cosmetic.
 *
 * Drop-in replacement for clearAccessToken() at a 401 handler.
 */
export function clearSessionAfter401(): void {
  // If there is no token now, the request that just 401'd cannot have carried
  // one — nothing to tear down, and nothing that should look like a logout.
  if (getAccessToken() === null) return;
  clearAccessToken();
}
